import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductAutocomplete = ({ value, onChange, onSelectSuggestion, placeholder = 'Type product name...' }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `${API_URL}/api/inventory/suggestions?query=${encodeURIComponent(query)}&limit=8`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Failed to fetch suggestions');
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [query, token]);

  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion.product_name);
    onChange(suggestion.product_name);
    setShowSuggestions(false);
    
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => query.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
      />

      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-[#6C757D]">Loading suggestions...</div>
          ) : suggestions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[#6C757D]">No suggestions found</div>
          ) : (
            suggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="px-4 py-3 cursor-pointer hover:bg-[#00B4D8] hover:bg-opacity-10 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#2B2D42]">{suggestion.product_name}</p>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-[#6C757D]">
                      {suggestion.last_price && (
                        <span>Price: ${suggestion.last_price.toFixed(2)}</span>
                      )}
                      {suggestion.last_supplier_name && (
                        <>
                          <span>•</span>
                          <span>Supplier: {suggestion.last_supplier_name}</span>
                        </>
                      )}
                      {suggestion.current_stock !== undefined && (
                        <>
                          <span>•</span>
                          <span>Stock: {suggestion.current_stock}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-[#6C757D] capitalize">
                    {suggestion.source}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;